import BigNumber from "bignumber.js";
import { get as _get, unionBy as _unionBy } from "lodash";
import { Pair } from "@koindx/v2-sdk";
import { CONFIG_BASE } from "../constants/configs";
import { fetcherToken } from "../utils/token";
import { mapSettledWithLimit, withRetry } from "../utils/async";
import { pair as pairService } from "../services/PairController";
import { PeripheryContract, CoreContract, defaultProvider } from "./contracts";
import LS from "./storage";

// pair addresses never change, they are cached in localStorage
const STORAGE_KEY = "pair_addresses";
// a missing pair can be created later, so it is only remembered for a while
const MISSING_PAIR_TTL = 5 * 60 * 1000;
const ZERO = new BigNumber(0);
// public RPC nodes rate limit bursts, reads run a few at a time and retry
const RPC_CONCURRENCY = 4;

const readStoredAddresses = () => {
  try {
    return JSON.parse(LS.getItemDevice(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
};

let pairAddresses = readStoredAddresses();
const missingPairs = new Map();

const pairKey = (tokenA, tokenB) => {
  const [a, b] = [tokenA.address.toLowerCase(), tokenB.address.toLowerCase()].sort();
  return `${CONFIG_BASE.chain.id}:${a}:${b}`;
};

const storePairAddress = (key, address) => {
  pairAddresses = { ...pairAddresses, [key]: address };
  try {
    LS.setItemDevice(STORAGE_KEY, JSON.stringify(pairAddresses));
  } catch (error) {
    console.log(error);
  }
};

/**
 * Returns the pair address of two tokens or null when the pair does not exist
 */
export const getPairAddress = async (tokenA, tokenB) => {
  const key = pairKey(tokenA, tokenB);
  if (pairAddresses[key]) {
    return pairAddresses[key];
  }
  const missingSince = missingPairs.get(key);
  if (missingSince && Date.now() - missingSince < MISSING_PAIR_TTL) {
    return null;
  }
  const { result } = await withRetry(() =>
    PeripheryContract().functions.get_pair({ tokenA: tokenA.address, tokenB: tokenB.address })
  );
  const address = _get(result, "value", null);
  if (address) {
    storePairAddress(key, address);
    missingPairs.delete(key);
  } else {
    missingPairs.set(key, Date.now());
  }
  return address;
};

/**
 * Same result as Fetcher.fetchPairData from @koindx/v2-sdk, but it reuses the cached pair
 * address, reads only the reserves and uses the configured RPC nodes
 * The total supply is not needed to quote trades, so it is left as zero
 */
export const fetchPair = async (tokenA, tokenB) => {
  const address = await getPairAddress(tokenA, tokenB);
  if (!address) {
    return new Pair(CONFIG_BASE.chain.id, "", defaultProvider, tokenA, tokenB, ZERO, ZERO, ZERO, ZERO, ZERO);
  }
  const { result } = await withRetry(() => CoreContract(address).functions.get_reserves());
  return new Pair(
    CONFIG_BASE.chain.id,
    address,
    defaultProvider,
    tokenA,
    tokenB,
    new BigNumber(_get(result, "kLast", 0)),
    new BigNumber(_get(result, "reserveA", 0)),
    new BigNumber(_get(result, "reserveB", 0)),
    new BigNumber(_get(result, "blockTime", 0)),
    ZERO
  );
};

/**
 * On-chain pairs of a route (consecutive tokens of the path), used to check a quote right before signing
 */
export const fetchRoutePairs = (path) =>
  Promise.all(path.slice(0, -1).map((token, index) => fetchPair(token, path[index + 1])));

// the SDK keeps reserve_0 for token_1 (see Pair.getOutputAmount), reserves are passed per token here
const buildPair = (address, tokenA, tokenB, reserveOfA, reserveOfB) => {
  const aIsToken0 = tokenA.sortsBefore(tokenB);
  return new Pair(
    CONFIG_BASE.chain.id,
    address,
    defaultProvider,
    tokenA,
    tokenB,
    ZERO,
    aIsToken0 ? reserveOfB : reserveOfA,
    aIsToken0 ? reserveOfA : reserveOfB,
    ZERO,
    ZERO
  );
};

// the API returns reserves with decimals ("291108.4131201"), the SDK works with raw amounts
const toRawAmount = (value, decimals) =>
  new BigNumber(value).shiftedBy(Number(decimals)).integerValue(BigNumber.ROUND_DOWN);

/**
 * Pair from the KoinDX API (/v2/pairs), or null when it does not match the tokens
 */
const pairFromApi = (data, tokenA, tokenB) => {
  const ids = [_get(data, "token0.id"), _get(data, "token1.id")];
  const reserves = [
    toRawAmount(_get(data, "reserve0"), _get(data, "token0.decimals", tokenA.decimals)),
    toRawAmount(_get(data, "reserve1"), _get(data, "token1.decimals", tokenB.decimals)),
  ];
  const indexA = ids.indexOf(tokenA.address);
  const indexB = ids.indexOf(tokenB.address);
  if (indexA === -1 || indexB === -1 || indexA === indexB || !reserves.every((reserve) => reserve.isFinite())) {
    return null;
  }
  return buildPair(data.id, tokenA, tokenB, reserves[indexA], reserves[indexB]);
};

// reserves of several pairs in one API request, keyed by pair address
const fetchApiPairs = async (addresses) => {
  if (!addresses.length) return {};
  try {
    const pairs = await pairService.getPairs(addresses);
    return pairs.reduce((byId, item) => ({ ...byId, [item.id]: item }), {});
  } catch (error) {
    console.log(error);
    return {};
  }
};

// tokens used as intermediate hops (constants/tokens.js "trade"), built once
let baseTokensRequest = null;
const getBaseTokens = () => {
  if (!baseTokensRequest) {
    const tokens = _get(CONFIG_BASE, "tokens.trade", []);
    baseTokensRequest = Promise.all(tokens.map((token) => fetcherToken(CONFIG_BASE.chain.id, token)));
  }
  return baseTokensRequest;
};

const samePair = ([a0, a1], [b0, b1]) =>
  (a0.address === b0.address && a1.address === b1.address) || (a0.address === b1.address && a1.address === b0.address);

/**
 * Loads the existing pairs that can route a trade between two tokens: the direct pair,
 * each token against the base tokens and the base tokens between them
 * Reserves come from one KoinDX API request when useApi is on, each pair missing there is read on-chain
 */
export const fetchCommonPairs = async (currencyA, currencyB, { useApi = true } = {}) => {
  const [tokenA, tokenB] = await Promise.all([
    fetcherToken(CONFIG_BASE.chain.id, currencyA),
    fetcherToken(CONFIG_BASE.chain.id, currencyB),
  ]);
  const bases = await getBaseTokens();

  const combinations = [
    [tokenA, tokenB],
    ...bases.map((base) => [tokenA, base]),
    ...bases.map((base) => [tokenB, base]),
    ...bases.flatMap((base) => bases.map((otherBase) => [base, otherBase])),
  ]
    .filter(([t0, t1]) => Boolean(t0 && t1) && t0.address !== t1.address)
    .reduce(
      (list, combination) => (list.some((item) => samePair(item, combination)) ? list : list.concat([combination])),
      []
    );

  // pair addresses are cached, only unknown pairs hit the chain
  const addresses = await mapSettledWithLimit(combinations, RPC_CONCURRENCY, ([t0, t1]) => getPairAddress(t0, t1));
  const existing = combinations
    .map((tokens, index) => ({
      tokens,
      address: addresses[index].status === "fulfilled" ? addresses[index].value : null,
    }))
    .filter((item) => item.address);

  const apiPairs = useApi ? await fetchApiPairs(existing.map((item) => item.address)) : {};
  const results = await mapSettledWithLimit(existing, RPC_CONCURRENCY, ({ tokens: [t0, t1], address }) => {
    const fromApi = apiPairs[address] ? pairFromApi(apiPairs[address], t0, t1) : null;
    return fromApi || fetchPair(t0, t1);
  });
  results.forEach((result, index) => {
    if (result.status === "rejected") {
      console.log(`Error with pair ${existing[index].tokens.map((token) => token.symbol).join("/")}:`, result.reason);
    }
  });
  const pairs = results
    .filter((result) => result.status === "fulfilled" && result.value.address)
    .map((result) => result.value);
  return _unionBy(pairs, "address");
};
